/**
 * Decision 0142 (messaging phase 1): threads (`T-n`) and the parties of a message. Pure.
 *
 * A thread is a conversation; its messages are comments with its id as `threadId`. Parties are agents, people, live
 * worker runs and a person's external participants (their ChatGPT dot, a coding session). Thread refs are numbered
 * after the old tasks' `T-n`, on their own counter, so item numbers don't move.
 */
import { agentOf, isRunEnded, runOf } from "./state"
import type { Actor, AgentId, EntityRef, Ids, MessageParty, ProjectState, Thread } from "./types"

/** The project's threads (absent = none). */
export const threadsOf = (s: ProjectState): ReadonlyArray<Thread> => s.threads ?? []

export const threadOf = (s: ProjectState, id: string): Thread | undefined =>
  threadsOf(s).find((t) => t.id === id)

/** A thread by its ref (`T-7`, case-insensitive) or id. */
export const threadByRef = (s: ProjectState, value: string): Thread | undefined => {
  const v = value.trim()
  const up = v.toUpperCase()
  return threadsOf(s).find((t) => t.id === v || t.ref === up)
}

/** The next thread ref: after every old task number and every thread so far. */
export const nextThreadRef = (s: ProjectState): Ids["ShortRef"] =>
  `T-${Math.max(s.refCounters.T, s.refCounters.threads ?? 0) + 1}` as Ids["ShortRef"]

/** A party's identity, for sets and comparisons. */
export const partyKey = (p: MessageParty): string => {
  switch (p._tag) {
    case "agent":
      return `a:${p.agentId}`
    case "user":
      return `u:${p.userId}`
    case "worker":
      return `w:${p.runId}`
    case "external":
      return `x:${p.participantId}`
  }
}

export const sameParty = (a: MessageParty, b: MessageParty): boolean => partyKey(a) === partyKey(b)

/** `ps` without duplicates, in order of first appearance. */
export const uniqueParties = (ps: ReadonlyArray<MessageParty>): ReadonlyArray<MessageParty> => {
  const seen = new Set<string>()
  const out: MessageParty[] = []
  for (const p of ps) {
    const k = partyKey(p)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(p)
  }
  return out
}

/** The party an actor writes as (`via`: a person's own external participant), or null (runners, the system). */
export const partyOfActor = (actor: Actor, via?: MessageParty | null): MessageParty | null => {
  switch (actor._tag) {
    case "user":
      return via ?? { _tag: "user", userId: actor.userId }
    case "agent":
      return { _tag: "agent", agentId: actor.agentId }
    case "worker":
      return { _tag: "worker", runId: actor.runId, agentId: actor.agentId }
    default:
      return null
  }
}

/** The person a party speaks for (a person, or the owner of their external participant); null for agents and runs. */
export const personOfParty = (p: MessageParty): Ids["UserId"] | null =>
  p._tag === "user" ? p.userId : p._tag === "external" ? p.ownerId : null

/** Is this worker or validator run live (a message steers it)? */
export const isLiveRun = (s: ProjectState, runId: Ids["RunId"]): boolean => {
  const r = runOf(s, runId)
  return r !== undefined && !isRunEnded(r) && r.cancelRequestedAt === undefined
}

/** A party in words: "@gia", "Jannik", "@jannik.dot", "W-31". */
export const partyName = (s: ProjectState, p: MessageParty): string => {
  switch (p._tag) {
    case "agent":
      return `@${agentOf(s, p.agentId)?.handle ?? p.agentId}`
    case "user":
      return s.members.find((m) => m.userId === p.userId)?.displayName ?? "a person"
    case "worker": {
      const r = runOf(s, p.runId)
      return r?.worker?.ref ?? r?.ref ?? "a worker"
    }
    case "external": {
      const owner = s.members.find((m) => m.userId === p.ownerId)?.handle
      return owner ? `@${owner}.${p.handle}` : `@${p.handle}`
    }
  }
}

/** Two `on` refs name the same record (or both none). */
export const sameOn = (a: EntityRef | null, b: EntityRef | null): boolean =>
  a === null || b === null ? a === b : a._tag === b._tag && a.id === b.id

/**
 * The open thread to continue for a new message `on` a record (or none) between exactly these parties, newest first;
 * undefined when there is none (a new thread opens).
 */
export const threadFor = (
  s: ProjectState,
  on: EntityRef | null,
  parties: ReadonlyArray<MessageParty>,
): Thread | undefined => {
  const want = new Set(parties.map(partyKey))
  return [...threadsOf(s)]
    .reverse()
    .find(
      (t) =>
        sameOn(t.on, on) && t.parties.length === want.size && t.parties.every((p) => want.has(partyKey(p))),
    )
}

/** The agent that hears about a message it sent: the agent itself, a worker's owner agent, else nobody. */
export const senderAgentOf = (actor: Actor): AgentId | null =>
  actor._tag === "agent" || actor._tag === "worker" ? actor.agentId : null

/** At most this many characters of a message go to an external participant in its event (the rest: read_messages). */
export const MESSAGE_EXCERPT_MAX = 4_000

/** " in T-7" for a message's thread, "" when it has none. */
export const threadRefOf = (s: ProjectState, threadId: string): string => {
  const t = threadOf(s, threadId)
  return t ? ` in ${t.ref}` : ""
}

export const messageExcerpt = (body: string): string =>
  body.length <= MESSAGE_EXCERPT_MAX ? body : `${body.slice(0, MESSAGE_EXCERPT_MAX - 1)}…`
